import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

export const CALENDAR_TIME_ZONE = 'America/Sao_Paulo';
export const CALENDAR_TIME_ZONE_LABEL = 'Horário de Brasília · São Paulo';

export const calendarInstant = value => {
  if (value instanceof Date) return new Date(value);
  if (!value) return new Date(NaN);
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(String(value))
    ? new Date(value)
    : zonedTimeToUtc(value, CALENDAR_TIME_ZONE);
};
export const calendarISO = value => {
  const date = calendarInstant(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};
export const calendarFormat = (value, options) => {
  const date = calendarInstant(value);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('pt-BR', {
        ...options,
        timeZone: CALENDAR_TIME_ZONE,
        hourCycle: 'h23',
      }).format(date);
};
export const calendarDateKey = value =>
  formatInTimeZone(calendarInstant(value), CALENDAR_TIME_ZONE, 'yyyy-MM-dd');
export const calendarTimeValue = value =>
  formatInTimeZone(calendarInstant(value), CALENDAR_TIME_ZONE, 'HH:mm');
export const calendarParts = value => {
  const date = calendarDateKey(value);
  const [year, month, day] = date.split('-').map(Number);
  return {
    year,
    month: month - 1,
    day,
    weekday: new Date(`${date}T12:00:00Z`).getUTCDay(),
    hour: Number(calendarTimeValue(value).slice(0, 2)),
  };
};
export const calendarAt = (date, time = '00:00:00.000') =>
  zonedTimeToUtc(`${date}T${time}`, CALENDAR_TIME_ZONE);
export const calendarDate = (year, month, day = 1) => {
  const civil = new Date(Date.UTC(year, month, day));
  return calendarAt(civil.toISOString().slice(0, 10));
};
export const calendarDayStart = value => calendarAt(calendarDateKey(value));
export const calendarDayEnd = value =>
  calendarAt(calendarDateKey(value), '23:59:59.999');
export const shiftCalendarDays = (value, amount) => {
  const { year, month, day } = calendarParts(value);
  return calendarDate(year, month, day + amount);
};
export const shiftCalendarMonths = (value, amount, firstDay = false) => {
  const { year, month, day } = calendarParts(value);
  const lastDay = new Date(Date.UTC(year, month + amount + 1, 0)).getUTCDate();
  return calendarDate(
    year,
    month + amount,
    firstDay ? 1 : Math.min(day, lastDay)
  );
};

// The date picker uses browser-local Date objects for civil dates, not instants.
export const calendarPickerDate = value => {
  if (!value) return null;
  const { year, month, day } = calendarParts(value);
  return new Date(year, month, day, 12);
};
export const pickerDateKey = value =>
  value instanceof Date && !Number.isNaN(value.getTime())
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';

export const moveCalendarPeriod = (event, targetDate, hour = null) => {
  const start = calendarInstant(event.starts_at);
  const end = event.ends_at ? calendarInstant(event.ends_at) : null;
  const day = calendarDateKey(targetDate);
  if (event.all_day) {
    const span = end
      ? Math.round(
          (Date.parse(`${calendarDateKey(end)}T00:00:00Z`) -
            Date.parse(`${calendarDateKey(start)}T00:00:00Z`)) /
            86400000
        )
      : 0;
    return {
      starts_at: calendarAt(day).toISOString(),
      ends_at: calendarDayEnd(
        shiftCalendarDays(targetDate, span)
      ).toISOString(),
    };
  }
  const clock = formatInTimeZone(start, CALENDAR_TIME_ZONE, 'HH:mm:ss.SSS');
  const moved = calendarAt(
    day,
    hour === null ? clock : `${String(hour).padStart(2, '0')}${clock.slice(2)}`
  );
  return {
    starts_at: moved.toISOString(),
    ends_at: end
      ? new Date(
          moved.getTime() + end.getTime() - start.getTime()
        ).toISOString()
      : '',
  };
};
